import React, {useEffect, useState} from "react";
import {Page, Text, View, Document, Image, Line, Svg, pdf} from "@react-pdf/renderer";
import {Button, Grid} from "@mui/material";
import {useFormContext} from "react-hook-form";
import Html from 'react-pdf-html';
import {useQuery} from "@apollo/client";
import {
    GetDonationCertificateByIdDocument
} from "../../../../../../packages/graphql-layer/src/queries/__generated__/GetDonationCertificateById";
import {sosDonationCertificate} from "../../../../page_layouts/pageLayoutsUtils";
import {imageToBase64, svgToDataUri} from "../../../../../utility/imageUtil";
import {
    FIRSTNAME,
    GIFT_CERTIFICATE_PDF_DATA,
    GIFT_FIRSTNAME,
    GIFT_PERSONAL_GREETING,
    GIFT_SURNAME,
    PRODUCT_FIRSTNAME,
    PRODUCT_GREETING, PRODUCT_QUANTITY,
    PRODUCT_SURNAME,
    SURNAME,
    useTranslate
} from "../../utils";
import {SosDonationCertificate} from "../../../../../../packages/graphql-layer/src";
import {QRCodeSVG} from "qrcode.react";
import {renderToString} from "react-dom/server";
import {initializeWithFallback} from "../../../../../utility/stringUtil";
import PdfWaterMark from "../../../../../utility/pdfWaterMark";
import {giftCertificateStyle, productStyleSheet} from "./GiftCertificatePdfStyle";

interface Props {
    isValid?: boolean,
    pdfCertificate: string | undefined;
    qrCodeLink?: string;
    isProduct?: boolean;
}

export type GiftCertificatePdfData = {
    background: string;
    title: string;
    description: string;
    personalGreeting: string;
    productInfo: string;
    productIcon: string;
    binder: string;
    viewType: string;
};

const REQUIRED_VALUES = [FIRSTNAME, SURNAME, GIFT_FIRSTNAME, GIFT_SURNAME];

export function createProductPdf(pdfData: GiftCertificatePdfData, withWatermark: boolean, qrCodeData?: string) {
    const svgDataUri = initializeWithFallback(qrCodeData);
    return <Document>
        <Page size='A4'>
            <View style={giftCertificateStyle.backgroundContainer}>
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <Image style={giftCertificateStyle.backgroundImage} src={pdfData.background}/>
            </View>
            {withWatermark && <PdfWaterMark text='MUSTER'/>}
            <View style={giftCertificateStyle.productBody}>
                <View style={giftCertificateStyle.productTitle}>
                    <Text>{pdfData.title}</Text>
                    <Svg height={40}>
                        <Line x1='0' y1='10' x2='140' y2='10' strokeWidth={5} stroke="rgb(93, 161, 39)"/>
                    </Svg>
                </View>
                <View style={giftCertificateStyle.productContent}>
                    <Html stylesheet={productStyleSheet}>{pdfData.description}</Html>
                    <Text style={giftCertificateStyle.personalGreeting}>{pdfData.personalGreeting}</Text>
                </View>
                <View style={giftCertificateStyle.product}>
                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                    { svgDataUri !== "" ? <Image style={giftCertificateStyle.productIcon} src={svgDataUri}/> : <Image style={giftCertificateStyle.productIcon} src={pdfData.productIcon}/> }
                    <Html style={giftCertificateStyle.productInfo}>{pdfData.productInfo}</Html>
                </View>
                <Html style={giftCertificateStyle.binder}>{pdfData.binder}</Html>
            </View>
        </Page>
    </Document>;
}


const replacePlaceholdersInTemplate = (description: string, getValues: (field: string) => any): string => {
    return description
        .replace("${firstname}", getValues(FIRSTNAME) || "")
        .replace("${surname}", getValues(SURNAME) || "")
        .replace("${giftFirstname}", getValues(GIFT_FIRSTNAME) || "")
        .replace("${giftSurname}", getValues(GIFT_SURNAME) || "")
        .replace("${productFirstname}", getValues(PRODUCT_FIRSTNAME) || "")
        .replace("${productQuantity}", getValues(PRODUCT_QUANTITY) || "")
        .replace("${productSurname}", getValues(PRODUCT_SURNAME) || "")
        .replace("${productGreeting}", getValues(PRODUCT_GREETING) || "")
        .replace('${priority}', getValues('priority') || "")
        .replace("${date}", new Date().toLocaleDateString(
            "de", {year: "numeric", month: "2-digit", day: "2-digit"})
        );
};

export const createPdfData = (
    getValues: (field: string) => any,
    content: SosDonationCertificate,
    background: string,
    icon: string
): GiftCertificatePdfData => {
    const description = replacePlaceholdersInTemplate(content?.detailText?.text || "", getValues);
    const personalGreeting = getValues(GIFT_PERSONAL_GREETING) || "";
    const binder = replacePlaceholdersInTemplate(content?.binder?.text || "", getValues);

    return {
        background,
        title: content?.title || "Certificate",
        description,
        personalGreeting,
        productInfo: content?.productInfo?.text || "",
        productIcon: icon,
        binder,
        viewType: content.viewtype!
    };
}

export function createPdf(pdfData: GiftCertificatePdfData, withWatermark: boolean, qrCodeData?: string) {
    const svgDataUri = initializeWithFallback(qrCodeData);
    return <Document>
        <Page size='A4'>
            <View style={giftCertificateStyle.backgroundContainer}>
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <Image style={giftCertificateStyle.backgroundImage} src={pdfData.background}/>
            </View>
            {withWatermark && <PdfWaterMark text='MUSTER'/>}
            <View style={giftCertificateStyle.body}>
                <View style={giftCertificateStyle.title}>
                    <Text>{pdfData.title}</Text>
                    <Svg height={40}>
                        <Line x1='0' y1='10' x2='140' y2='10' strokeWidth={6} stroke="rgb(93, 161, 39)"/>
                    </Svg>
                </View>
                <View style={giftCertificateStyle.content}>
                    <Html style={giftCertificateStyle.description}>{pdfData.description}</Html>
                    <Text style={giftCertificateStyle.personalGreeting}>{pdfData.personalGreeting}</Text>
                </View>
                <View style={giftCertificateStyle.product}>
                    {/* eslint-disable-next-line jsx-a11y/alt-text */}
                    { svgDataUri !== "" ? <Image style={giftCertificateStyle.productIcon} src={svgDataUri}/> : <Image style={giftCertificateStyle.productIcon} src={pdfData.productIcon}/> }
                    <Html style={giftCertificateStyle.productInfo}>{pdfData.productInfo}</Html>
                </View>
                <Html style={giftCertificateStyle.binder}>{pdfData.binder}</Html>
            </View>
        </Page>
    </Document>;
}


export async function convertSvgToDataUri(qrString: string) {
    const svg = (
        <QRCodeSVG value={qrString} />
    )
    return await svgToDataUri(renderToString(svg));
}

const GiftCertificatePreview: React.FC<Props> = ({pdfCertificate, isValid, qrCodeLink, isProduct}) => {
    const translate = useTranslate("gift_data");
    const {getValues, setValue} = useFormContext();
    const [content, setContent] = React.useState<SosDonationCertificate | null>(null);
    const [background, setBackground] = React.useState<string | null>(null);
    const [icon, setIcon] = React.useState<string | null>(null);
    const [svgDataUri, setSvgDataUri] = useState<string>("");
    const qrString = qrCodeLink || "";

    useEffect(() => {
        if (qrString !== "") {
            const dataUri = convertSvgToDataUri(qrString);
            dataUri.then((dataUri) => setSvgDataUri(dataUri || ""))
        }
    }, [qrString])

    const contentId = pdfCertificate?.split('/')?.pop();

    useQuery(GetDonationCertificateByIdDocument, {
        variables: {contentId},
        skip: !contentId,
        fetchPolicy: "network-only",
        onError: (error) => console.error("Error loading content: ", error),
        onCompleted: (data) => {
            const contentData = data?.content?.content;
            if (contentData?.type === sosDonationCertificate) {
                setContent(contentData);
                imageToBase64(contentData?.picture?.uriTemplate, setBackground);
                imageToBase64(contentData?.globalFactsSettings?.teaser_icon, setIcon);
            }
        }
    });

    useEffect(() => {
        if (!content) {
            const previewContent = getValues('previewContent');
            if (previewContent) {
                setContent(previewContent);
                imageToBase64(previewContent?.picture?.uriTemplate, setBackground);
                imageToBase64(previewContent?.globalFactsSettings?.teaser_icon, setIcon);
            }
        }
        if (content && background && icon) {
            setValue(GIFT_CERTIFICATE_PDF_DATA, {content, background, icon});
        }
    }, [content, background, icon, setValue, getValues]);

    const createPdfData = (
        getValues: (field: string) => any,
        content: SosDonationCertificate,
        background: string,
        icon: string
    ): GiftCertificatePdfData => {
        const description = replacePlaceholdersInTemplate(content?.detailText?.text || "", getValues);
        const personalGreeting = getValues(GIFT_PERSONAL_GREETING) || "";
        const binder = replacePlaceholdersInTemplate(content?.binder?.text || "", getValues);

        return {
            background,
            title: content?.title || "Certificate",
            description,
            personalGreeting,
            productInfo: content?.productInfo?.text || "",
            productIcon: icon,
            binder,
            viewType: content?.viewtype || ""
        };
    }


    const openPdfPreview = () => {
        if (content && background && icon) {
            const pdfData = createPdfData(getValues, content, background, icon);
            pdf(createPdf(pdfData, true)).toBlob().then(blob => window.open(URL.createObjectURL(blob)));
        }
    };

    const openPdfPreviewForProduct = () => {
        if (content && background && icon) {
            const pdfData = createPdfData(getValues, content, background, icon);
            pdf(createProductPdf(pdfData, true, svgDataUri)).toBlob().then(blob => window.open(URL.createObjectURL(blob)));
        }
    };

    const invalidRequiredValues = () => REQUIRED_VALUES.some(field => !getValues(field));
    const isButtonDisabled = !content || !background || !icon || invalidRequiredValues();

    return (
        <>
            {isProduct ?
                    <Button type="button"
                            data-cypress="giftCertificatePreview"
                            size="large"
                            variant="contained"
                            onClick={openPdfPreviewForProduct}
                            disabled={!isValid}
                            data-testid="giftCertificateProductPreview"
                            fullWidth>{translate('p020_product_label_Submit')!}</Button> :
                    <Grid item xs={12} md={12}>
                        <Button
                            data-cypress="giftCertificatePreview"
                            variant="contained"
                            onClick={openPdfPreview}
                            disabled={isButtonDisabled}
                        >{translate("gift_certificate_preview")}</Button>
                    </Grid>
             }
        </>
    );
};

export default GiftCertificatePreview;
